import { useEffect, useState } from "react";
import { PROPERTIES as FILE_PROPERTIES } from "../data/properties";
import { filterByVerification, normaliseProperty } from "../data/propertyModel";
import { api } from "./api";

/**
 * useProperties() — returns the current merged property inventory.
 *
 * Data flow:
 *   1. Start with file-based properties (immediate render, no flash).
 *   2. Fetch admin overrides from `GET /api/properties/overrides` (map: slug → override diff).
 *   3. Merge each override on top of its matching file record.
 *   4. Re-run `filterByVerification()` so any admin-promoted demo→verified record
 *      surfaces on the production hostname, and any admin-demoted verified→demonstration
 *      record disappears in production.
 *
 * The hook is safe to call from many components; overrides are fetched only
 * once per page load and shared via module-scoped cache.
 */

let _cache = null;   // Promise or resolved overrides map
let _tickCounter = 0;
const _subscribers = new Set();

function invalidate() {
  _cache = null;
  _tickCounter += 1;
  for (const cb of _subscribers) cb(_tickCounter);
}

async function fetchOverrides() {
  if (_cache) return _cache;
  _cache = api.get("/properties/overrides")
    .then((r) => r?.data?.items || {})
    .catch(() => ({})); // Silent — file records still render.
  return _cache;
}

export function useProperties() {
  const [merged, setMerged] = useState(() => FILE_PROPERTIES);
  const [overrides, setOverrides] = useState({});
  const [tick, setTick] = useState(_tickCounter);

  useEffect(() => {
    _subscribers.add(setTick);
    return () => _subscribers.delete(setTick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchOverrides().then((map) => {
      if (cancelled) return;
      setOverrides(map);
      const applied = FILE_PROPERTIES.map((p) => {
        const diff = map[p.slug];
        if (!diff) return p;
        // Merge scalar fields; if gallery is provided in the override use it as the whole gallery.
        return normaliseProperty({ ...p, ...diff, gallery: diff.gallery || p.gallery, images: diff.gallery || p.images });
      });
      // Also include any pure-override records that don't have a file counterpart
      // (rare — usually only after a fully-admin-created property).
      const knownSlugs = new Set(FILE_PROPERTIES.map((p) => p.slug));
      for (const slug of Object.keys(map)) {
        if (!knownSlugs.has(slug)) {
          applied.push(normaliseProperty({ ...map[slug], slug }));
        }
      }
      setMerged(filterByVerification(applied));
    });
    return () => { cancelled = true; };
  }, [tick]);

  return { properties: merged, overrides, refresh: invalidate };
}

export function invalidateProperties() { invalidate(); }
